import type { FetchLike } from "./http";
import { apiUrl } from "./apiUrl";
import { API_BASE_URL } from "@/api/base";

/**
 * Renewing the session before it expires, scheduled `REFRESH_LEAD_MS` ahead of `exp`.
 *
 * The credential here is the REFRESH token, not the access token: `POST /auth/refresh` is `auth: false`
 * and reads `{ refreshToken }` from the JSON body, because the access token it renews may already have
 * expired — that is the point. The answer carries a fresh access token AND a fresh refresh token: the
 * old one is single-use, and presenting it twice revokes the session as a theft, so the caller must
 * store the rotated one.
 *
 * 🔴 **ONE APP ISSUES SESSIONS, SO A REFRESH HAS ONE PLACE TO GO** (D83, and PNK-106).
 * This used to be a choice. Pinksy signed a password sign-in (D53) and the Passport signed a passkey
 * or an assist, their refresh tokens were rows in different databases, and a token presented to the
 * wrong one came back 401 — read by everything downstream as "your session ended", and the reason
 * every password session died at fifteen minutes. `refreshBaseFor` read the access token's `kid` to
 * pick a base. The Passport is gone, so the base is `API_BASE_URL` and the bug has nowhere to live.
 */
export const REFRESH_PATH = "/auth/refresh";
/** How far ahead of expiry the renewal is attempted. */
export const REFRESH_LEAD_MS = 60_000;
/** Never fire a renewal sooner than this after it is scheduled — a token about to expire is left to expire. */
export const MIN_REFRESH_DELAY_MS = 5_000;
/** `setTimeout` treats anything above 2^31−1 ms as 0; a long-lived token must not refresh immediately. */
export const MAX_TIMER_MS = 2 ** 31 - 1;

export type RefreshResult =
  | { ok: true; token: string; refreshToken: string }
  | { ok: false; reason: "rejected" | "failed"; message: string };

interface RefreshBody {
  token?: unknown;
  refreshToken?: unknown;
}

/**
 * Renew a session with `refreshToken` — the one being replaced is sent explicitly, never whatever
 * happens to be held. The rotated refresh token comes back with the access token and MUST be stored
 * in its place; a refused refresh (`401`) means the session is over.
 */
export async function refreshSessionToken(
  refreshToken: string,
  base: string = API_BASE_URL,
  fetchImpl: FetchLike = (i, o) => fetch(i, o),
): Promise<RefreshResult> {
  let res: Response;
  try {
    res = await fetchImpl(apiUrl(REFRESH_PATH, base), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
  } catch (err) {
    return { ok: false, reason: "failed", message: err instanceof Error ? err.message : String(err) };
  }
  if (res.status === 401 || res.status === 403) return { ok: false, reason: "rejected", message: `refresh refused: ${res.status}` };
  if (!res.ok) return { ok: false, reason: "failed", message: `refresh failed: ${res.status}` };
  let body: RefreshBody;
  try {
    body = (await res.json()) as RefreshBody;
  } catch {
    return { ok: false, reason: "failed", message: "refresh answer is not JSON" };
  }
  if (typeof body.token !== "string" || body.token === "") return { ok: false, reason: "failed", message: "refresh answer carries no token" };
  if (typeof body.refreshToken !== "string" || body.refreshToken === "") return { ok: false, reason: "failed", message: "refresh answer carries no refresh token" };
  return { ok: true, token: body.token, refreshToken: body.refreshToken };
}

export const SIGN_OUT_PATH = "/auth/sign-out";

/**
 * 🔴 **END THE SESSION ON THE SERVER, TOO** (D83).
 *
 * Forgetting the tokens locally is not signing out — the refresh row stays live for its full day, and
 * a copy taken from `localStorage` would still renew against it. This revokes the row named by the
 * `sid` claim on the caller's own token.
 *
 * Best-effort and never awaited by the UI: signing out is immediate on screen whatever the network
 * does, and a session that survives on the server because the person was offline will lapse on its
 * own at `expiresAt`.
 */
export async function endSessionOnServer(accessToken: string | null, fetchImpl: FetchLike = (i, o) => fetch(i, o)): Promise<boolean> {
  if (accessToken === null || accessToken === "") return false;
  try {
    const res = await fetchImpl(apiUrl(SIGN_OUT_PATH), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: "{}",
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Milliseconds until the renewal should run — clamped so it is never immediate and never overflows a timer. */
export function refreshDelayMs(expiresAtMs: number, nowMs: number = Date.now()): number {
  return Math.min(Math.max(expiresAtMs - REFRESH_LEAD_MS - nowMs, MIN_REFRESH_DELAY_MS), MAX_TIMER_MS);
}

/** Milliseconds until the session lapses — 0 when it already has. */
export function expiryDelayMs(expiresAtMs: number, nowMs: number = Date.now()): number {
  return Math.min(Math.max(expiresAtMs - nowMs, 0), MAX_TIMER_MS);
}

/** The two timers a live session keeps: renew ahead of `exp`, lapse at `exp`. One instance per shell. */
export interface SessionTimers {
  schedule(expiresAtMs: number, onRefresh: () => void, onExpire: () => void, nowMs?: number): void;
  cancel(): void;
  /** Tests: whether anything is pending. */
  pending(): boolean;
}

export function createSessionTimers(): SessionTimers {
  let refresh: ReturnType<typeof setTimeout> | null = null;
  let expire: ReturnType<typeof setTimeout> | null = null;
  const cancel = (): void => {
    if (refresh !== null) clearTimeout(refresh);
    if (expire !== null) clearTimeout(expire);
    refresh = null;
    expire = null;
  };
  return {
    schedule(expiresAtMs, onRefresh, onExpire, nowMs = Date.now()) {
      cancel();
      const untilExpiry = expiryDelayMs(expiresAtMs, nowMs);
      // A renewal only makes sense while there is still room for it before the lapse.
      if (untilExpiry > MIN_REFRESH_DELAY_MS) refresh = setTimeout(onRefresh, refreshDelayMs(expiresAtMs, nowMs));
      expire = setTimeout(onExpire, untilExpiry);
    },
    cancel,
    pending: () => refresh !== null || expire !== null,
  };
}
